import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  Client,
  CreateClientInput,
  UpdateClientInput,
} from '~/Services/Clients'

export interface GetClientsPayload {
  onSuccess?: (clients: Client[]) => void
  onFailure?: (error: string) => void
}

export interface GetClientByIdPayload {
  clientId: string
  onSuccess?: (client: Client) => void
  onFailure?: (error: string) => void
}

export interface CreateClientPayload {
  data: CreateClientInput
  onSuccess?: (client: Client) => void
  onFailure?: (error: string) => void
}

export interface UpdateClientPayload {
  clientId: string
  data: UpdateClientInput
  onSuccess?: (client: Client) => void
  onFailure?: (error: string) => void
}

export interface ClientReducerStore {
  listClientsData: Client[]
  selectedClient: Client | null
  loading: boolean
  detailLoading: boolean
  submitting: boolean
  error: string | null
}

const initialState: ClientReducerStore = {
  listClientsData: [],
  selectedClient: null,
  loading: false,
  detailLoading: false,
  submitting: false,
  error: null,
}

export const ClientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    getClientsRequest: (
      state,
      _action: PayloadAction<GetClientsPayload | undefined>,
    ) => {
      state.loading = true
      state.error = null
    },
    getClientsSuccess: (state, action: PayloadAction<Client[]>) => {
      state.listClientsData = action.payload
      state.loading = false
      state.error = null
    },
    getClientsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    getClientByIdRequest: (
      state,
      _action: PayloadAction<GetClientByIdPayload>,
    ) => {
      state.detailLoading = true
      state.error = null
    },
    getClientByIdSuccess: (state, action: PayloadAction<Client>) => {
      state.selectedClient = action.payload
      state.detailLoading = false
      state.error = null
    },
    getClientByIdFailure: (state, action: PayloadAction<string>) => {
      state.detailLoading = false
      state.error = action.payload
    },

    createClientRequest: (
      state,
      _action: PayloadAction<CreateClientPayload>,
    ) => {
      state.submitting = true
      state.error = null
    },
    createClientSuccess: (state, action: PayloadAction<Client>) => {
      state.listClientsData = [action.payload, ...state.listClientsData]
      state.selectedClient = action.payload
      state.submitting = false
      state.error = null
    },
    createClientFailure: (state, action: PayloadAction<string>) => {
      state.submitting = false
      state.error = action.payload
    },

    updateClientRequest: (
      state,
      _action: PayloadAction<UpdateClientPayload>,
    ) => {
      state.submitting = true
      state.error = null
    },
    updateClientSuccess: (state, action: PayloadAction<Client>) => {
      const updatedClient = action.payload
      const clientIndex = state.listClientsData.findIndex(
        client => client.clientId === updatedClient.clientId,
      )

      if (clientIndex !== -1) {
        state.listClientsData[clientIndex] = updatedClient
      }

      if (state.selectedClient?.clientId === updatedClient.clientId) {
        state.selectedClient = updatedClient
      }

      state.submitting = false
      state.error = null
    },
    updateClientFailure: (state, action: PayloadAction<string>) => {
      state.submitting = false
      state.error = action.payload
    },

    clearSelectedClient: state => {
      state.selectedClient = null
    },
    clearClientError: state => {
      state.error = null
    },
  },
})

export const ClientActions = ClientSlice.actions
export const ClientReducer = ClientSlice.reducer
